/**
 * Route Redirect Component
 * Handles legacy route redirects based on route_map.csv
 */

import { useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { resolveLegacyPrefixRedirect } from '@/config/legacyRoutePrefixes';

// Route redirect mappings from route_map.csv
const ROUTE_REDIRECTS: Record<string, { newRoute: string; type: number }> = {
  '/': { newRoute: '/dashboard', type: 301 },
  '/sales/sales-orders': { newRoute: '/growth/orders', type: 301 },
  '/projects/list': { newRoute: '/projects', type: 301 },
  '/crm/estimations': { newRoute: '/estimation/dashboard', type: 301 },
  '/modules/settings': { newRoute: '/settings', type: 302 },
  '/settings': { newRoute: '/settings/system', type: 302 },
  '/procurement/grns': { newRoute: '/scm/procurement/grn', type: 302 },
};

/** Route element for legacy module prefixes; keeps the remaining path, query string, hash and navigation state. */
export const LegacyPrefixRedirect: React.FC = () => {
  const location = useLocation();
  const target = resolveLegacyPrefixRedirect(location.pathname);
  // Never render null (blank Outlet). Fall back to home if prefix mapping somehow misses.
  const to = target
    ? `${target}${location.search}${location.hash}`
    : `/dashboard${location.search}${location.hash}`;
  return <Navigate to={to} state={location.state} replace />;
};

/**
 * RouteRedirect Component
 * Checks current route and redirects if it matches legacy patterns
 */
export const RouteRedirect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPath = location.pathname;
    const redirect = ROUTE_REDIRECTS[currentPath];

    if (redirect) {
      // Use navigate for client-side redirect
      navigate(redirect.newRoute, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

/**
 * RedirectRoute Component
 * Used in Routes to define specific redirect rules
 */
interface RedirectRouteProps {
  from: string;
  to: string;
  permanent?: boolean;
}

export const RedirectRoute: React.FC<RedirectRouteProps> = ({
  from,
  to,
  permanent = true,
}) => {
  return <Navigate to={to} replace={permanent} />;
};

export default RouteRedirect;
